import ContactPicker from './common/ContactPicker';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Calendar, Clock, MapPin, FileText, Link as LinkIcon, Sparkles,
  Zap, AlertTriangle, RefreshCw, Video, Bell, Briefcase, Search,
  ChevronDown, Check, Wand2, Brain, Target, Users, UserPlus, Mail,
  User, Building2, Clock as ClockIcon
} from 'lucide-react';

const CATEGORY_COLORS = {
  consultation: '#14b8a6',
  court: '#ef4444',
  meeting: '#3b82f6',
  deadline: '#f59e0b',
  deposition: '#8b5cf6',
  personal: '#22c55e',
  focus: '#06b6d4',
  other: '#6366f1',
};

const CATEGORY_LABELS = {
  consultation: 'Consultation',
  court: 'Court',
  meeting: 'Meeting',
  deadline: 'Deadline',
  deposition: 'Deposition',
  personal: 'Personal',
  focus: 'Focus Time',
  other: 'Other',
};

function CategoryDropdown({ value, onChange, size = 'normal' }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const py = size === 'small' ? 'py-1.5 px-2.5' : 'py-2.5 px-3';
  const textSize = size === 'small' ? 'text-xs' : 'text-sm';
  const dotSize = size === 'small' ? 8 : 10;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full ${py} bg-slate-800/50 border border-cyan-500/30 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 ${textSize} flex items-center justify-between gap-2`}
      >
        <span className="flex items-center gap-2">
          <span style={{ width: dotSize, height: dotSize, borderRadius: '50%', backgroundColor: CATEGORY_COLORS[value] || CATEGORY_COLORS.other, display: 'inline-block', flexShrink: 0 }} />
          {CATEGORY_LABELS[value] || value}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-cyan-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-slate-800 border border-cyan-500/30 rounded-lg shadow-xl overflow-hidden" style={{ maxHeight: 240, overflowY: 'auto' }}>
          {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
            <button
              key={cat}
              type="button"
              onClick={() => { onChange(cat); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 ${size === 'small' ? 'py-1.5' : 'py-2'} ${textSize} text-left transition-colors ${
                value === cat ? 'bg-cyan-500/20 text-white' : 'text-cyan-200 hover:bg-slate-700'
              }`}
            >
              <span style={{ width: dotSize, height: dotSize, borderRadius: '50%', backgroundColor: color, display: 'inline-block', flexShrink: 0 }} />
              {CATEGORY_LABELS[cat] || cat}
              {value === cat && <Check className="w-3.5 h-3.5 ml-auto text-cyan-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CreateEventModal = ({ isOpen, onClose, onEventCreated, existingEvents = [], calendarUsers = [], defaultOwnerId = null, defaultCategory = 'meeting', defaultRecurrence = 'none', defaultTitle = '', defaultDate = null }) => {
  const [useNaturalLanguage, setUseNaturalLanguage] = useState(false); // Default to manual mode
  const [naturalLanguageText, setNaturalLanguageText] = useState(defaultTitle);
  const [selectedOwnerId, setSelectedOwnerId] = useState(defaultOwnerId);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

  // Default times for focus blocks (morning 8-10 AM) or from defaultDate
  const getDefaultTimes = (forDate = null) => {
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // If a specific date was provided (from double-click)
    if (forDate) {
      const startDate = new Date(forDate);
      startDate.setHours(9, 0, 0, 0); // Default to 9 AM
      const endDate = new Date(forDate);
      endDate.setHours(10, 0, 0, 0); // Default 1 hour duration
      return {
        start: formatDate(startDate),
        end: formatDate(endDate)
      };
    }

    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(8, 0, 0, 0);
    const endTime = new Date(tomorrow);
    endTime.setHours(10, 0, 0, 0);

    return {
      start: formatDate(tomorrow),
      end: formatDate(endTime)
    };
  };

  const defaultTimes = defaultDate ? getDefaultTimes(defaultDate) : getDefaultTimes();

  const [formData, setFormData] = useState({
    title: defaultTitle || (defaultCategory === 'focus' ? 'Focus Time' : ''),
    description: defaultCategory === 'focus' ? 'Dedicated time for deep work - no meetings or interruptions' : '',
    location: '',
    start_time: defaultTimes.start,
    end_time: defaultTimes.end,
    all_day: false,
    category: defaultCategory,
    case_id: '',
    attendees: [],
    is_virtual: false,
    meeting_link: '',
    reminder_minutes: 15,
    recurrence: defaultRecurrence,
    recurrence_interval: 1,
    priority: 'normal'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [cases, setCases] = useState([]);
  const [showCaseDropdown, setShowCaseDropdown] = useState(false);
  const [caseSearch, setCaseSearch] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [showAttendeeDropdown, setShowAttendeeDropdown] = useState(false);
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [externalSuggestions, setExternalSuggestions] = useState([]);
  const [showExternalSuggestions, setShowExternalSuggestions] = useState(false);
  const [externalSuggestionsLoading, setExternalSuggestionsLoading] = useState(false);
  const [selectedExternalIndex, setSelectedExternalIndex] = useState(-1);
  const externalDebounceRef = useRef(null);
  const externalInputRef = useRef(null);

  // Quick event templates
  const quickTemplates = [
    { icon: Video, label: 'Client Call', text: 'Schedule a client call tomorrow at 10am for 30 minutes', category: 'consultation' },
    { icon: Briefcase, label: 'Court Date', text: 'Court appearance next week', category: 'court' },
    { icon: Target, label: 'Deadline', text: 'Filing deadline', category: 'deadline' },
    { icon: Brain, label: 'Strategy Meeting', text: 'Team strategy meeting this Friday at 2pm', category: 'meeting' },
    { icon: Target, label: 'Focus Time', text: 'Focus time block for deep work', category: 'focus' },
  ];

  useEffect(() => {
    if (isOpen) {
      loadCases();
      setSelectedOwnerId(defaultOwnerId);
      // Reset form with new defaults when modal opens
      const times = defaultDate ? getDefaultTimes(defaultDate) : getDefaultTimes();
      setFormData({
        title: defaultTitle || (defaultCategory === 'focus' ? 'Focus Time' : ''),
        description: defaultCategory === 'focus' ? 'Dedicated time for deep work - no meetings or interruptions' : '',
        location: '',
        start_time: times.start,
        end_time: times.end,
        all_day: false,
        category: defaultCategory,
            case_id: '',
        attendees: [],
        is_virtual: false,
        meeting_link: '',
        reminder_minutes: 15,
        recurrence: defaultRecurrence,
        recurrence_interval: 1,
        priority: 'normal'
      });
      setUseNaturalLanguage(false); // Default to manual mode
      setNaturalLanguageText(defaultTitle);
    }
  }, [isOpen, defaultOwnerId, defaultCategory, defaultRecurrence, defaultTitle, defaultDate]);

  const getSelectedOwnerName = () => {
    if (!selectedOwnerId) return 'Myself';
    const user = calendarUsers.find(u => u.id === selectedOwnerId);
    if (!user) return 'Myself';
    return user.full_name || user.display_name || user.username;
  };

  // Check for conflicts when times change
  useEffect(() => {
    if (formData.start_time && formData.end_time) {
      checkConflicts();
    }
  }, [formData.start_time, formData.end_time]);

  // AI suggestions based on natural language input
  useEffect(() => {
    if (naturalLanguageText.length > 10 && useNaturalLanguage) {
      const timer = setTimeout(() => {
        generateAiSuggestions();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [naturalLanguageText]);

  const loadCases = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/virgo/cases?status=active', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCases(data.cases || data || []);
      }
    } catch (err) {
      console.error('Error loading cases:', err);
    }
  };

  const checkConflicts = () => {
    const start = new Date(formData.start_time);
    const end = new Date(formData.end_time);

    const overlapping = existingEvents.filter(event => {
      const eventStart = new Date(event.start_time);
      const eventEnd = new Date(event.end_time);
      return (start < eventEnd && end > eventStart);
    });

    setConflicts(overlapping);
  };

  const generateAiSuggestions = async () => {
    // Mock AI suggestions - in production this would call the AI endpoint
    const suggestions = [];
    const text = naturalLanguageText.toLowerCase();

    if (text.includes('meeting') || text.includes('call')) {
      suggestions.push('Add video conferencing link?');
    }
    if (text.includes('deadline') || text.includes('due')) {
      suggestions.push('Set high priority?');
      suggestions.push('Add 24-hour reminder?');
    }
    if (text.includes('court') || text.includes('hearing')) {
      suggestions.push('Block travel time before?');
      suggestions.push('Prepare documents checklist?');
    }

    setAiSuggestions(suggestions);
    setShowSuggestions(suggestions.length > 0);
  };

  const handleTemplateClick = (template) => {
    setNaturalLanguageText(template.text);
    setFormData({ ...formData, category: template.category });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      let response;

      if (useNaturalLanguage) {
        // Find the selected case to get client name
        const selectedCase = formData.case_id ? cases.find(c => c.id === formData.case_id) : null;

        response = await fetch('/api/calendar/events/natural-language', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            text: naturalLanguageText,
            user_id: 1,
            category: formData.category,
            priority: formData.priority,
            is_virtual: formData.is_virtual,
            owner_id: selectedOwnerId || undefined,
            case_id: formData.case_id || undefined,
            client_name: selectedCase?.client_name || undefined,
            attendees: selectedAttendees
          })
        });
      } else {
        // Convert recurrence option to is_recurring and recurrence_rule
        const isRecurring = formData.recurrence !== 'none';
        let recurrenceRule = null;

        if (isRecurring) {
          const startDate = new Date(formData.start_time);
          const dayOfWeek = startDate.getDay();
          const dayNames = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
          const interval = formData.recurrence_interval || 1;

          switch (formData.recurrence) {
            case 'daily':
              recurrenceRule = { frequency: 'DAILY', interval: interval };
              break;
            case 'weekdays':
              recurrenceRule = { frequency: 'WEEKLY', interval: interval, byDay: ['MO', 'TU', 'WE', 'TH', 'FR'] };
              break;
            case 'weekly':
              recurrenceRule = { frequency: 'WEEKLY', interval: interval, byDay: [dayNames[dayOfWeek]] };
              break;
            case 'monthly':
              recurrenceRule = { frequency: 'MONTHLY', interval: interval, byMonthDay: startDate.getDate() };
              break;
            case 'yearly':
              recurrenceRule = { frequency: 'YEARLY', interval: interval };
              break;
          }
        }

        response = await fetch('/api/calendar/events', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...formData,
            is_recurring: isRecurring,
            recurrence_rule: recurrenceRule,
            owner_id: selectedOwnerId || undefined,
            attendees: selectedAttendees,
            reminders: [{ minutes_before: formData.reminder_minutes, reminder_type: 'notification' }]
          })
        });
      }

      if (response.ok) {
        const event = await response.json();
        onEventCreated(event);
        onClose();
        resetForm();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create event');
      }
    } catch (err) {
      setError('Failed to create event. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      start_time: '',
      end_time: '',
      all_day: false,
      category: 'meeting',
      case_id: '',
      attendees: [],
      is_virtual: false,
      meeting_link: '',
      reminder_minutes: 15,
      recurrence: 'none',
      recurrence_interval: 1,
      priority: 'normal'
    });
    setNaturalLanguageText('');
    setConflicts([]);
    setAiSuggestions([]);
    setSelectedAttendees([]);
    setAttendeeSearch('');
    setExternalEmail('');
  };

  const filteredCases = cases.filter(c => {
    const searchTerm = caseSearch.toLowerCase();
    const caseName = c.case_name?.toLowerCase() || '';
    const caseNumber = String(c.case_number || '').toLowerCase();
    const displayNumber = c.display_number?.toLowerCase() || '';
    const clientName = c.client_name?.toLowerCase() || '';
    return caseName.includes(searchTerm) ||
           caseNumber.includes(searchTerm) ||
           displayNumber.includes(searchTerm) ||
           clientName.includes(searchTerm);
  });

  // Filter users for attendee selection (exclude already selected)
  const filteredAttendees = calendarUsers.filter(user => {
    const searchTerm = attendeeSearch.toLowerCase();
    const isAlreadySelected = selectedAttendees.some(a => a.user_id === user.id);
    const fullName = user.full_name?.toLowerCase() || '';
    const displayName = user.display_name?.toLowerCase() || '';
    const username = user.username?.toLowerCase() || '';
    const email = user.email?.toLowerCase() || '';

    return !isAlreadySelected && (
      fullName.includes(searchTerm) ||
      displayName.includes(searchTerm) ||
      username.includes(searchTerm) ||
      email.includes(searchTerm)
    );
  });

  const addUserAttendee = (user) => {
    setSelectedAttendees([...selectedAttendees, {
      user_id: user.id,
      email: user.email,
      name: user.full_name || user.display_name || user.username,
      role: 'attendee',
      is_optional: false,
      notify: true
    }]);
    setAttendeeSearch('');
    setShowAttendeeDropdown(false);
  };

  const addExternalAttendee = () => {
    if (externalEmail && externalEmail.includes('@')) {
      setSelectedAttendees([...selectedAttendees, {
        user_id: null,
        email: externalEmail,
        name: externalEmail.split('@')[0],
        role: 'attendee',
        is_optional: false,
        notify: true
      }]);
      setExternalEmail('');
    }
  };

  const removeAttendee = (index) => {
    setSelectedAttendees(selectedAttendees.filter((_, i) => i !== index));
  };

  // Fetch external email suggestions from API
  const fetchExternalSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setExternalSuggestions([]);
      return;
    }

    try {
      setExternalSuggestionsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/mail/compose/recipients/suggest?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter out already selected emails
        const selectedEmails = selectedAttendees.map(a => a.email.toLowerCase());
        const filtered = (data.suggestions || []).filter(
          s => !selectedEmails.includes(s.email.toLowerCase())
        );
        setExternalSuggestions(filtered);
      }
    } catch (err) {
      console.error('Failed to fetch email suggestions:', err);
      setExternalSuggestions([]);
    } finally {
      setExternalSuggestionsLoading(false);
    }
  }, [selectedAttendees]);

  // Debounced external email search
  useEffect(() => {
    if (externalDebounceRef.current) {
      clearTimeout(externalDebounceRef.current);
    }

    if (externalEmail) {
      externalDebounceRef.current = setTimeout(() => {
        fetchExternalSuggestions(externalEmail);
      }, 200);
    } else {
      setExternalSuggestions([]);
    }

    return () => {
      if (externalDebounceRef.current) {
        clearTimeout(externalDebounceRef.current);
      }
    };
  }, [externalEmail, fetchExternalSuggestions]);

  // Handle keyboard navigation for external suggestions
  const handleExternalKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedExternalIndex(prev =>
        prev < externalSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedExternalIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedExternalIndex >= 0 && externalSuggestions[selectedExternalIndex]) {
        selectExternalSuggestion(externalSuggestions[selectedExternalIndex]);
      } else if (externalEmail && externalEmail.includes('@')) {
        addExternalAttendee();
      }
    } else if (e.key === 'Tab' && externalEmail) {
      if (selectedExternalIndex >= 0 && externalSuggestions[selectedExternalIndex]) {
        e.preventDefault();
        selectExternalSuggestion(externalSuggestions[selectedExternalIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowExternalSuggestions(false);
    }
  };

  // Select a suggestion from the dropdown
  const selectExternalSuggestion = (suggestion) => {
    setSelectedAttendees([...selectedAttendees, {
      user_id: null,
      email: suggestion.email,
      name: suggestion.name || suggestion.email.split('@')[0],
      role: 'attendee',
      is_optional: false,
      notify: true
    }]);
    setExternalEmail('');
    setExternalSuggestions([]);
    setShowExternalSuggestions(false);
    setSelectedExternalIndex(-1);
    externalInputRef.current?.focus();
  };

  // Get icon for suggestion source
  const getSourceIcon = (source) => {
    switch (source) {
      case 'contact':
        return <User className="w-3.5 h-3.5 text-cyan-400" />;
      case 'staff':
        return <Building2 className="w-3.5 h-3.5 text-cyan-400" />;
      case 'history':
        return <ClockIcon className="w-3.5 h-3.5 text-amber-400" />;
      default:
        return <Users className="w-3.5 h-3.5 text-gray-400" />;
    }
  };

  // Get label for suggestion source
  const getSourceLabel = (source) => {
    switch (source) {
      case 'contact':
        return 'Client';
      case 'staff':
        return 'Staff';
      case 'history':
        return 'Recent';
      default:
        return source;
    }
  };

  const categoryColors = CATEGORY_COLORS;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-slate-900 via-slate-900/50 to-slate-900 border border-cyan-500/30 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header with gradient */}
        <div className="relative px-6 py-5 border-b border-cyan-500/20">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10" />
          <div className="relative flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Create Event</h2>
                <p className="text-sm text-cyan-300">AI-powered scheduling</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-cyan-300 hover:text-white hover:bg-cyan-500/20 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Mode Toggle */}
          <div className="mb-6 flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-cyan-500/20 via-blue-500/10 to-cyan-500/20 border border-cyan-500/30">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${useNaturalLanguage ? 'bg-gradient-to-br from-cyan-500 to-blue-500' : 'bg-slate-700'}`}>
                {useNaturalLanguage ? <Wand2 className="w-4 h-4 text-white" /> : <Calendar className="w-4 h-4 text-cyan-300" />}
              </div>
              <div>
                <span className="text-sm font-medium text-white">
                  {useNaturalLanguage ? 'AI Mode' : 'Manual Mode'}
                </span>
                <p className="text-xs text-cyan-400">
                  {useNaturalLanguage ? 'Describe your event naturally' : 'Fill in the form fields'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setUseNaturalLanguage(!useNaturalLanguage)}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-all ${
                useNaturalLanguage
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500'
                  : 'bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                  useNaturalLanguage ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Create For (Owner) Selector */}
          {calendarUsers.length > 0 && (
            <div className="mb-6">
              <label className="block text-xs font-medium text-cyan-400 mb-2 uppercase tracking-wide">
                Create Event For
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowOwnerDropdown(!showOwnerDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/50 border border-cyan-500/30 rounded-xl hover:bg-slate-700/50 transition-colors"
                >
                  <span className="text-white font-medium">{getSelectedOwnerName()}</span>
                  <ChevronDown className={`w-4 h-4 text-cyan-400 transition-transform ${showOwnerDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showOwnerDropdown && (
                  <div className="absolute z-20 w-full mt-2 bg-slate-800 border border-cyan-500/30 rounded-xl shadow-2xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedOwnerId(null);
                        setShowOwnerDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        !selectedOwnerId ? 'bg-cyan-500/20 text-white' : 'text-cyan-200 hover:bg-slate-700'
                      }`}
                    >
                      Myself
                    </button>
                    <div className="border-t border-cyan-500/20"></div>
                    {calendarUsers.map(user => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setSelectedOwnerId(user.id);
                          setShowOwnerDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          selectedOwnerId === user.id ? 'bg-cyan-500/20 text-white' : 'text-cyan-200 hover:bg-slate-700'
                        }`}
                      >
                        <span className="font-medium">{user.full_name || user.display_name || user.username}</span>
                        <span className="text-cyan-400 text-xs ml-2">({user.role})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Templates - Only show in AI mode */}
          {useNaturalLanguage && (
            <div className="mb-6">
              <p className="text-xs font-medium text-cyan-400 mb-3 uppercase tracking-wide">Quick Templates</p>
              <div className="grid grid-cols-2 gap-2">
                {quickTemplates.map((template, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleTemplateClick(template)}
                    className="flex items-center gap-2 p-3 rounded-xl bg-slate-800/50 border border-cyan-500/20 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all text-left group"
                  >
                    <div className="p-2 rounded-lg bg-cyan-500/20 group-hover:bg-cyan-500/30">
                      <template.icon className="w-4 h-4 text-cyan-400" />
                    </div>
                    <span className="text-sm text-cyan-200">{template.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 rounded-xl bg-red-500/20 border border-red-500/30 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Conflict Warning */}
          {conflicts.length > 0 && (
            <div className="mb-4 p-4 rounded-xl bg-amber-500/20 border border-amber-500/30">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <span className="text-amber-300 font-medium">Scheduling Conflict Detected</span>
              </div>
              <div className="space-y-1">
                {conflicts.map((event, idx) => (
                  <p key={idx} className="text-sm text-amber-200/70">
                    Overlaps with: {event.title}
                  </p>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {useNaturalLanguage ? (
              /* AI Natural Language Input */
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-cyan-200 mb-2">
                    Describe your event
                  </label>
                  <div className="relative">
                    <textarea
                      value={naturalLanguageText}
                      onChange={(e) => setNaturalLanguageText(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent placeholder-cyan-400/50 resize-none"
                      rows="4"
                      placeholder="e.g., Schedule a client consultation with John Smith next Tuesday at 2pm for 1 hour at the downtown office"
                      required
                    />
                    {aiProcessing && (
                      <div className="absolute right-3 top-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-cyan-500 border-t-transparent" />
                      </div>
                    )}
                  </div>

                  {/* AI Suggestions */}
                  {showSuggestions && aiSuggestions.length > 0 && (
                    <div className="mt-3 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                      <p className="text-xs font-medium text-cyan-400 mb-2 flex items-center gap-1">
                        <Brain className="w-3 h-3" /> AI Suggestions
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {aiSuggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="text-xs px-3 py-1.5 rounded-full bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 transition-all"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Options for AI Mode */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-cyan-400 mb-2">Category</label>
                    <CategoryDropdown value={formData.category} onChange={(cat) => setFormData({ ...formData, category: cat })} size="small" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-cyan-400 mb-2">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-800/50 border border-cyan-500/30 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 text-sm"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-cyan-400 mb-2">Type</label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_virtual: !formData.is_virtual })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm flex items-center justify-center gap-2 transition-all ${
                        formData.is_virtual
                          ? 'bg-cyan-500/30 border-cyan-500 text-cyan-200'
                          : 'bg-slate-800/50 border-cyan-500/30 text-cyan-400'
                      }`}
                    >
                      <Video className="w-4 h-4" />
                      {formData.is_virtual ? 'Virtual' : 'In Person'}
                    </button>
                  </div>
                </div>

                {/* Case Link */}
                <div className="relative">
                  <label className="block text-xs font-medium text-cyan-400 mb-2">
                    <Briefcase className="w-3 h-3 inline mr-1" />
                    Link to Case (Optional)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={caseSearch}
                      onChange={(e) => {
                        setCaseSearch(e.target.value);
                        setShowCaseDropdown(true);
                      }}
                      onFocus={() => setShowCaseDropdown(true)}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-cyan-500/30 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 placeholder-cyan-400/50 text-sm"
                      placeholder="Search cases..."
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />
                  </div>

                  {showCaseDropdown && filteredCases.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-slate-800 border border-cyan-500/30 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                      {filteredCases.slice(0, 5).map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, case_id: c.id });
                            setCaseSearch(c.case_name || c.case_number);
                            setShowCaseDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-cyan-500/20 transition-all flex items-center justify-between"
                        >
                          <span className="text-sm text-white">{c.case_name || 'Unnamed Case'}</span>
                          <span className="text-xs text-cyan-400">{c.case_number}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Invite Attendees */}
                <div className="mt-4">
                  <label className="block text-xs font-medium text-cyan-400 mb-2">
                    <Users className="w-3 h-3 inline mr-1" />
                    Invite Attendees (Optional)
                  </label>

                  {/* Selected Attendees */}
                  {selectedAttendees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {selectedAttendees.map((attendee, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 border border-cyan-500/30 rounded-full text-sm"
                        >
                          <span className="text-cyan-200">{attendee.name || attendee.email}</span>
                          <button
                            type="button"
                            onClick={() => removeAttendee(idx)}
                            className="text-cyan-400 hover:text-red-400 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add from Users */}
                  <div className="relative mb-2">
                    <input
                      type="text"
                      value={attendeeSearch}
                      onChange={(e) => {
                        setAttendeeSearch(e.target.value);
                        setShowAttendeeDropdown(true);
                      }}
                      onFocus={() => setShowAttendeeDropdown(true)}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-cyan-500/30 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 placeholder-cyan-400/50 text-sm"
                      placeholder="Search team members..."
                    />
                    <UserPlus className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />

                    {showAttendeeDropdown && filteredAttendees.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-slate-800 border border-cyan-500/30 rounded-lg shadow-xl max-h-40 overflow-y-auto">
                        {filteredAttendees.slice(0, 5).map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => addUserAttendee(user)}
                            className="w-full px-4 py-2 text-left hover:bg-cyan-500/20 transition-all flex items-center justify-between"
                          >
                            <div>
                              <span className="text-sm text-white">{user.full_name || user.display_name || user.username}</span>
                              <span className="text-xs text-cyan-400 ml-2">({user.role})</span>
                            </div>
                            <Mail className="w-3 h-3 text-cyan-400" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add client/contact by name (tenant-scoped picker; raw email only as external fallback) */}
                  <ContactPicker
                    placeholder="Search contacts by name, email, or phone…"
                    allowRawEmail
                    excludeEmails={selectedAttendees.map(a => a.email).filter(Boolean)}
                    onSelect={(person) => setSelectedAttendees(prev => ([...prev, {
                      user_id: null,
                      email: person.email,
                      name: person.name,
                      role: 'attendee',
                      is_optional: false,
                      notify: true
                    }]))}
                  />

                  {selectedAttendees.length > 0 && (
                    <p className="text-xs text-cyan-400 mt-2 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Invitations will be sent via email
                    </p>
                  )}
                </div>
              </div>
            ) : (
              /* Manual Form */
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-cyan-200 mb-2">
                    Event Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent placeholder-cyan-400/50"
                    placeholder="Team Meeting"
                    required
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-cyan-200 mb-2">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Start Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-cyan-200 mb-2">
                      End Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                </div>

                {/* Location & Virtual Toggle */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-cyan-200 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 placeholder-cyan-400/50"
                      placeholder={formData.is_virtual ? 'Meeting link will be generated' : 'Conference Room A'}
                      disabled={formData.is_virtual}
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-sm font-medium text-cyan-200 mb-2">Type</label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_virtual: !formData.is_virtual })}
                      className={`w-full px-4 py-3 rounded-xl border flex items-center justify-center gap-2 transition-all ${
                        formData.is_virtual
                          ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 border-cyan-500 text-white'
                          : 'bg-slate-800/50 border-cyan-500/30 text-cyan-300'
                      }`}
                    >
                      <Video className="w-4 h-4" />
                      {formData.is_virtual ? 'Virtual' : 'In Person'}
                    </button>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-cyan-200 mb-2">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 placeholder-cyan-400/50"
                    rows="3"
                    placeholder="Add details about the event..."
                  />
                </div>

                {/* Category & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-cyan-200 mb-2">Category</label>
                    <CategoryDropdown value={formData.category} onChange={(cat) => setFormData({ ...formData, category: cat })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-cyan-200 mb-2">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                {/* Reminder & Recurrence */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-cyan-200 mb-2">
                      <Bell className="w-4 h-4 inline mr-1" />
                      Reminder
                    </label>
                    <select
                      value={formData.reminder_minutes}
                      onChange={(e) => setFormData({ ...formData, reminder_minutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value={0}>No reminder</option>
                      <option value={5}>5 minutes before</option>
                      <option value={15}>15 minutes before</option>
                      <option value={30}>30 minutes before</option>
                      <option value={60}>1 hour before</option>
                      <option value={1440}>1 day before</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-cyan-200 mb-2">
                      <RefreshCw className="w-4 h-4 inline mr-1" />
                      Recurrence
                    </label>
                    <select
                      value={formData.recurrence}
                      onChange={(e) => setFormData({ ...formData, recurrence: e.target.value, recurrence_interval: 1 })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="none">Does not repeat</option>
                      <option value="daily">Daily</option>
                      <option value="weekdays">Weekdays</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>

                {/* Custom Interval - Only show when recurrence is not 'none' */}
                {formData.recurrence !== 'none' && formData.recurrence !== 'weekdays' && (
                  <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                    <label className="block text-sm font-medium text-cyan-200 mb-3">
                      Repeat every
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={formData.recurrence_interval}
                        onChange={(e) => setFormData({ ...formData, recurrence_interval: Math.max(1, Math.min(99, parseInt(e.target.value) || 1)) })}
                        className="w-20 px-3 py-2 bg-slate-800/50 border border-cyan-500/30 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 text-center"
                      />
                      <span className="text-cyan-200">
                        {formData.recurrence === 'daily' && (formData.recurrence_interval === 1 ? 'day' : 'days')}
                        {formData.recurrence === 'weekly' && (formData.recurrence_interval === 1 ? 'week' : 'weeks')}
                        {formData.recurrence === 'monthly' && (formData.recurrence_interval === 1 ? 'month' : 'months')}
                        {formData.recurrence === 'yearly' && (formData.recurrence_interval === 1 ? 'year' : 'years')}
                      </span>
                    </div>
                    <p className="text-xs text-cyan-400 mt-2">
                      {formData.recurrence_interval === 1
                        ? `Repeats every ${formData.recurrence.replace('ly', '')}`
                        : `Repeats every ${formData.recurrence_interval} ${formData.recurrence === 'daily' ? 'days' : formData.recurrence === 'weekly' ? 'weeks' : formData.recurrence === 'monthly' ? 'months' : 'years'}`
                      }
                    </p>
                  </div>
                )}

                {/* Case Link */}
                <div className="relative">
                  <label className="block text-sm font-medium text-cyan-200 mb-2">
                    <LinkIcon className="w-4 h-4 inline mr-1" />
                    Link to Case (Optional)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={caseSearch}
                      onChange={(e) => {
                        setCaseSearch(e.target.value);
                        setShowCaseDropdown(true);
                      }}
                      onFocus={() => setShowCaseDropdown(true)}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 placeholder-cyan-400/50"
                      placeholder="Search cases..."
                    />
                    <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />
                  </div>

                  {showCaseDropdown && filteredCases.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-slate-800 border border-cyan-500/30 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                      {filteredCases.slice(0, 5).map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, case_id: c.id });
                            setCaseSearch(c.case_name || c.case_number);
                            setShowCaseDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-cyan-500/20 transition-all flex items-center justify-between"
                        >
                          <span className="text-white">{c.case_name || 'Unnamed Case'}</span>
                          <span className="text-sm text-cyan-400">{c.case_number}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Invite Attendees */}
                <div>
                  <label className="block text-sm font-medium text-cyan-200 mb-2">
                    <Users className="w-4 h-4 inline mr-1" />
                    Invite Attendees (Optional)
                  </label>

                  {/* Selected Attendees */}
                  {selectedAttendees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {selectedAttendees.map((attendee, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 border border-cyan-500/30 rounded-full text-sm"
                        >
                          <span className="text-cyan-200">{attendee.name || attendee.email}</span>
                          <button
                            type="button"
                            onClick={() => removeAttendee(idx)}
                            className="text-cyan-400 hover:text-red-400 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add from Users */}
                  <div className="relative mb-2">
                    <input
                      type="text"
                      value={attendeeSearch}
                      onChange={(e) => {
                        setAttendeeSearch(e.target.value);
                        setShowAttendeeDropdown(true);
                      }}
                      onFocus={() => setShowAttendeeDropdown(true)}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 placeholder-cyan-400/50"
                      placeholder="Search team members..."
                    />
                    <UserPlus className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />

                    {showAttendeeDropdown && filteredAttendees.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-slate-800 border border-cyan-500/30 rounded-xl shadow-xl max-h-40 overflow-y-auto">
                        {filteredAttendees.slice(0, 5).map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => addUserAttendee(user)}
                            className="w-full px-4 py-3 text-left hover:bg-cyan-500/20 transition-all flex items-center justify-between"
                          >
                            <div>
                              <span className="text-white">{user.full_name || user.display_name || user.username}</span>
                              <span className="text-sm text-cyan-400 ml-2">({user.role})</span>
                            </div>
                            <Mail className="w-4 h-4 text-cyan-400" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add client/contact by name (tenant-scoped picker; raw email only as external fallback) */}
                  <ContactPicker
                    placeholder="Search contacts by name, email, or phone…"
                    allowRawEmail
                    excludeEmails={selectedAttendees.map(a => a.email).filter(Boolean)}
                    onSelect={(person) => setSelectedAttendees(prev => ([...prev, {
                      user_id: null,
                      email: person.email,
                      name: person.name,
                      role: 'attendee',
                      is_optional: false,
                      notify: true
                    }]))}
                  />

                  {selectedAttendees.length > 0 && (
                    <p className="text-xs text-cyan-400 mt-2 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Invitations will be sent via email
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-cyan-200 bg-slate-800/50 border border-cyan-500/30 rounded-xl hover:bg-cyan-500/10 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-5 py-2.5 text-white rounded-xl transition-all flex items-center gap-2 ${
                  useNaturalLanguage
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400'
                    : 'bg-cyan-600 hover:bg-cyan-500'
                } disabled:opacity-50 shadow-lg shadow-cyan-500/25`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Creating...
                  </>
                ) : (
                  <>
                    {useNaturalLanguage && <Sparkles className="w-4 h-4" />}
                    Create Event
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEventModal;
